import { createThumbor } from 'thumbor-client'
import { ImageMode, ImageQuality } from './imageExtension'

const CDN_URL = 'https://i.factcool.com'
const THUMBOR_URL = CDN_URL + '/cache3'
export const NO_IMAGE_URL = CDN_URL + '/no_image.png'

export const hasThumborKey = (): boolean => !!process.env.THUMBOR_PUBLIC_KEY

const getThumbor = () => {
  return createThumbor({
    url: THUMBOR_URL,
    key: process.env.THUMBOR_PUBLIC_KEY
  })
}

const isProcessedUrl = (url: string): boolean => {
  return url.includes('/cache3/') || url.includes('/unsafe/')
}

/**
 * Creates a clean image path from a URL and extracts dimensions if present
 * @param url - The image URL to process
 * @returns Object containing the clean path and optional dimensions
 */
const createImagePath = (
  url: string
): {
  path: string
  width?: number
  height?: number
} => {
  let imagePath = url
  let width: number | undefined
  let height: number | undefined

  // Extract pathname from factcool URLs
  if (url.includes('i.factcool.com')) {
    try {
      const urlObj = new URL(url)
      imagePath = urlObj.pathname.replace(/^\//, '')
    } catch {
      // catch any errors in URL parsing
    }
  }

  // Remove leading slash
  imagePath = imagePath.replace(/^\//, '')

  // Remove cache2/dimensions/ prefix (e.g., "cache2/410x615/catalog/...")
  const cache2Match = imagePath.match(/^cache2\/(\d+)x(\d+)\/(.+)$/)
  if (cache2Match) {
    width = parseInt(cache2Match[1])
    height = parseInt(cache2Match[2])
    imagePath = cache2Match[3]
  } else {
    // Extract dimensions from path (e.g., "600x400/image.jpg")
    const dimensionMatch = imagePath.match(/^(\d+)x(\d+)\/(.+)$/)
    if (dimensionMatch) {
      width = parseInt(dimensionMatch[1])
      height = parseInt(dimensionMatch[2])
      imagePath = dimensionMatch[3]
    }
  }

  return {
    path: imagePath,
    width,
    height
  }
}

/**
 * @param url
 * @param options
 */
export const applyImagePolicy = (
  url: string,
  options: {
    useCdn?: boolean
    quality?: ImageQuality | number
    width?: number
    height?: number
    size?: { width: number; height: number }
    mode?: ImageMode
    useFitIn?: boolean
    fill?: string
  } = {}
): string => {
  if (!url) {
    return NO_IMAGE_URL
  }

  if (url.startsWith('data:')) {
    return url
  }

  if (isProcessedUrl(url)) {
    return url
  }

  const thumbor = getThumbor()

  try {
    const {
      path: imagePath,
      width: pathWidth,
      height: pathHeight
    } = createImagePath(url)

    const width = options.width || options.size?.width || pathWidth
    const height = options.height || options.size?.height || pathHeight

    let thumborBuilder = thumbor.fromUrl(imagePath)

    if (width && height) {
      if (options.useFitIn) {
        thumborBuilder = thumborBuilder.fitIn(width, height)
      } else {
        thumborBuilder = thumborBuilder.resize(width, height)
      }
    } else if (width) {
      if (options.useFitIn) {
        thumborBuilder = thumborBuilder.fitIn(width, 0)
      } else {
        thumborBuilder = thumborBuilder.resize(width, 0)
      }
    } else if (height) {
      if (options.useFitIn) {
        thumborBuilder = thumborBuilder.fitIn(0, height)
      } else {
        thumborBuilder = thumborBuilder.resize(0, height)
      }
    }

    if (options.quality) {
      thumborBuilder = thumborBuilder.filter(`quality(${options.quality})`)
    }

    if (options.fill) {
      thumborBuilder = thumborBuilder.filter(`fill(${options.fill})`)
    }

    thumborBuilder = thumborBuilder.filter('format(webp)')

    const imgUrl = thumborBuilder.buildURL()

    return imgUrl
  } catch (error) {
    return NO_IMAGE_URL
  }
}
